// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Platform Admin Page
//
// platform_admin only. Three things a backstop admin needs: spin up new
// orgs, see/manage every org on the platform (the "god rights" rescue path
// for an org that's lost its own admins — reuses the existing per-org admin
// pages by just pointing activeOrgId at any org), and assign org/platform
// roles to existing users by email.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeToQuery, subscribeToDoc, callFunction, where, orderBy, limit, AppError,
  setDoc, updateDoc, doc, db }
  from '../../services/firebase';
import { AdminListSection } from '../../components/AdminListSection';

interface OrgListItem {
  id: string;
  name: string;
  joinMode: string;
  deliveryMode?: string;
  color?: string;
  categoryIds?: string[];
  adminUids?: string[];
  isSystem?: boolean;
  isStarterOrg?: boolean;
  monitoringFlag?: string | null;
}

interface CategoryItem {
  id: string;
  name: string;
}

interface OrgSignupRequest {
  id: string;
  orgName: string;
  categoryId: string;
  requesterName: string;
  requesterEmail: string;
  description: string;
  status: 'pending' | 'approved' | 'declined';
}

interface OrgAdminRequest {
  id: string;
  orgId: string;
  orgName: string;
  requesterName: string;
  requesterEmail: string;
  roleDescription: string;
  domainMismatchAcknowledged: boolean;
  status: 'pending' | 'approved' | 'declined';
}

interface ContentReport {
  id: string;
  contentType: string;
  contentId: string;
  orgId: string;
  orgName: string;
  contentTitle: string;
  reason: string;
  details: string | null;
  status: 'open' | 'reviewed' | 'resolved' | 'dismissed';
  reportedAt: number | null;
  reviewedAt: number | null;
}

const REPORT_REASON_LABELS: Record<string, string> = {
  spam: 'Spam or irrelevant',
  misleading: 'Misleading or false information',
  inappropriate: 'Inappropriate or offensive content',
  illegal: 'Appears to promote illegal activity',
  harassment: 'Harassment or threatening behavior',
  other: 'Other',
};

const REPORT_STATUSES = ['open', 'reviewed', 'resolved', 'dismissed'] as const;
type ReportStatus = typeof REPORT_STATUSES[number];

const INVITE_ROLES = ['admin', 'org_creator', 'org_approver'] as const;
type InviteRole = typeof INVITE_ROLES[number];

// Returns a friendly message when a user lookup fails because they haven't registered yet.
function friendlyUserError(err: unknown, email: string): string {
  const msg = (err as AppError).message ?? '';
  if (msg.toLowerCase().includes('user') && msg.toLowerCase().includes('not found')) {
    return `No account found for ${email}. They must register on the Aphilaty mobile app first, then you can assign their role here.`;
  }
  return msg;
}

const ORG_ROLES = ['member', 'admin', 'org_approver', 'offer_only'];
const PLATFORM_ROLES = ['member', 'platform_admin', 'CLO'];
const JOIN_MODES = ['listed', 'open', 'private'];

export function PlatformAdminPage() {
  const { setActiveOrgId, user, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<OrgListItem[]>([]);
  const [flaggedPrivateOrgs, setFlaggedPrivateOrgs] = useState<OrgListItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [orgFilter, setOrgFilter] = useState('');

  // Org signup requests (Path A — new org)
  const [signupRequests, setSignupRequests] = useState<OrgSignupRequest[]>([]);
  const [reviewingId,    setReviewingId]    = useState<string | null>(null);
  const [reviewColor,    setReviewColor]    = useState('#4a90d9');
  const [reviewJoinMode, setReviewJoinMode] = useState<'listed' | 'open' | 'private'>('listed');
  const [reviewNote,     setReviewNote]     = useState('');
  const [reviewError,    setReviewError]    = useState<string | null>(null);
  const [reviewSuccess,  setReviewSuccess]  = useState<string | null>(null);

  // Org admin requests (Path B — existing org, AD-10)
  const [orgAdminRequests,     setOrgAdminRequests]     = useState<OrgAdminRequest[]>([]);
  const [reviewingAdminReqId,  setReviewingAdminReqId]  = useState<string | null>(null);
  const [adminReqReviewNote,   setAdminReqReviewNote]   = useState('');
  const [adminReqError,        setAdminReqError]        = useState<string | null>(null);
  const [adminReqSuccess,      setAdminReqSuccess]      = useState<string | null>(null);

  // Content reports (A4/A5) — read via getContentReports CF (no client Firestore
  // access to content_reports; rules deny all reads by design)
  const [contentReports,      setContentReports]      = useState<ContentReport[]>([]);
  const [reportStatusFilter,  setReportStatusFilter]  = useState<ReportStatus>('open');
  const [isLoadingReports,    setIsLoadingReports]    = useState(false);
  const [reviewingReportId,   setReviewingReportId]   = useState<string | null>(null);
  const [reportError,         setReportError]         = useState<string | null>(null);
  const [reportSuccess,       setReportSuccess]       = useState<string | null>(null);

  // Create-org form
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgCategory, setNewOrgCategory] = useState('');
  const [newOrgColor, setNewOrgColor] = useState('#4a90d9');
  const [newOrgJoinMode, setNewOrgJoinMode] = useState<'listed' | 'open' | 'private'>('listed');
  const [newOrgDeliveryMode, setNewOrgDeliveryMode] = useState<'immediate' | 'batch'>('batch');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [createOrgError, setCreateOrgError] = useState<string | null>(null);
  const [createOrgSuccess, setCreateOrgSuccess] = useState<string | null>(null);

  // Admin roster expansion (per-org)
  const [expandedAdminOrgId, setExpandedAdminOrgId] = useState<string | null>(null);

  // Invite admin (per-org, Path B)
  const [inviteOrgId,    setInviteOrgId]    = useState<string | null>(null);
  const [inviteEmail,    setInviteEmail]    = useState('');
  const [inviteRole,     setInviteRole]     = useState<InviteRole>('admin');
  const [isInviting,     setIsInviting]     = useState(false);
  const [inviteError,    setInviteError]    = useState<string | null>(null);
  const [inviteSuccess,  setInviteSuccess]  = useState<string | null>(null);

  // Platform settings
  const [autoPostDelayInput, setAutoPostDelayInput] = useState('0');
  const [defaultDeliveryModeInput, setDefaultDeliveryModeInput] = useState<'' | 'immediate' | 'batch'>('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);

  // PII detection settings
  const [piiPhone, setPiiPhone] = useState<'warn_and_block' | 'warn_and_allow'>('warn_and_block');
  const [piiEmail, setPiiEmail] = useState<'warn_and_block' | 'warn_and_allow'>('warn_and_block');
  const [isSavingPii, setIsSavingPii] = useState(false);
  const [piiError, setPiiError] = useState<string | null>(null);
  const [piiSuccess, setPiiSuccess] = useState<string | null>(null);

  // User/role assignment form
  const [roleEmail, setRoleEmail] = useState('');
  const [roleOrgId, setRoleOrgId] = useState('');
  const [orgRole, setOrgRole] = useState<'member' | 'admin' | 'org_approver' | 'offer_only'>('member');
  const [platformRole, setPlatformRole] = useState<'member' | 'platform_admin' | 'CLO'>('member');
  const [isAssigningOrgRole, setIsAssigningOrgRole] = useState(false);
  const [isAssigningPlatformRole, setIsAssigningPlatformRole] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [roleSuccess, setRoleSuccess] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToDoc<{ autoPostDelaySeconds?: number; defaultDeliveryMode?: string }>(
      'platform_settings/defaults',
      data => {
        setAutoPostDelayInput(String(data?.autoPostDelaySeconds ?? 0));
        setDefaultDeliveryModeInput((data?.defaultDeliveryMode as '' | 'immediate' | 'batch') ?? '');
      },
      err => console.error('[platform_settings subscription]', err)
    );
  }, []);

  useEffect(() => {
    return subscribeToDoc<{ phone?: string; email?: string }>(
      'platform_settings/piiDetection',
      data => {
        setPiiPhone((data?.phone as 'warn_and_block' | 'warn_and_allow') ?? 'warn_and_block');
        setPiiEmail((data?.email as 'warn_and_block' | 'warn_and_allow') ?? 'warn_and_block');
      },
      err => console.error('[piiDetection subscription]', err)
    );
  }, []);

  useEffect(() => {
    return subscribeToQuery<OrgListItem>(
      'organizations',
      [where('isDeleted', '==', false), limit(200)],
      data => {
        // Platform-managed orgs: exclude user-created private orgs (joinMode:'user')
        // and auto-seeded starter orgs (isStarterOrg:true, e.g. My Family).
        // Both types of personal orgs are private and should not appear in routine browsing.
        const platformOrgs = data
          .filter(o => o.joinMode !== 'user' && !o.isStarterOrg)
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrgs(platformOrgs);

        // Flagged private orgs: any personal org (user or starter) with a monitoringFlag.
        // These surface here for safety/legal intervention only — not routine visibility.
        const flagged = data.filter(
          o => (o.joinMode === 'user' || o.isStarterOrg) && o.monitoringFlag
        );
        setFlaggedPrivateOrgs(flagged);
      },
      err => console.error('[orgs subscription]', err)
    );
  }, []);

  useEffect(() => {
    return subscribeToQuery<CategoryItem>('org_categories', [orderBy('name', 'asc')],
      data => setCategories(data));
  }, []);

  useEffect(() => {
    return subscribeToQuery<OrgSignupRequest>(
      'org_signup_requests',
      [where('status', '==', 'pending'), orderBy('createdAt', 'asc')],
      data => setSignupRequests(data),
      err => console.error('[org_signup_requests subscription]', err)
    );
  }, []);

  useEffect(() => {
    return subscribeToQuery<OrgAdminRequest>(
      'org_admin_requests',
      [where('status', '==', 'pending'), orderBy('createdAt', 'asc')],
      data => setOrgAdminRequests(data),
      err => console.error('[org_admin_requests subscription]', err)
    );
  }, []);

  const loadContentReports = async (status: ReportStatus) => {
    setIsLoadingReports(true);
    setReportError(null);
    try {
      const res = await callFunction<{ reports: ContentReport[] }>('getContentReports', { status });
      // callFunction unwraps result.data — fields are at the top level of res
      setContentReports(res?.reports ?? []);
    } catch (err) {
      setReportError((err as AppError).message ?? 'Failed to load content reports.');
    } finally {
      setIsLoadingReports(false);
    }
  };

  useEffect(() => {
    if (!isPlatformAdmin) return;
    loadContentReports(reportStatusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin, reportStatusFilter]);

  const filteredOrgs = orgs.filter(o =>
    !orgFilter.trim() || o.name.toLowerCase().includes(orgFilter.trim().toLowerCase())
    || o.id.toLowerCase().includes(orgFilter.trim().toLowerCase())
  );

  const handleSaveSettings = async () => {
    const seconds = parseInt(autoPostDelayInput, 10);
    if (isNaN(seconds) || seconds < 0) {
      setSettingsError('Delay must be a non-negative number of seconds.');
      return;
    }
    setIsSavingSettings(true); setSettingsError(null); setSettingsSuccess(null);
    try {
      await setDoc(doc(db, 'platform_settings/defaults'), {
        autoPostDelaySeconds: seconds,
        defaultDeliveryMode: defaultDeliveryModeInput || null,
      }, { merge: true });
      setSettingsSuccess(`Settings saved.`);
    } catch (err: unknown) {
      setSettingsError((err as Error).message ?? 'Failed to save settings.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSavePii = async () => {
    setIsSavingPii(true); setPiiError(null); setPiiSuccess(null);
    try {
      await callFunction('updatePiiDetectionSettings', { phone: piiPhone, email: piiEmail });
      setPiiSuccess('PII detection settings saved.');
    } catch (err: unknown) {
      setPiiError((err as Error).message ?? 'Failed to save PII settings.');
    } finally {
      setIsSavingPii(false);
    }
  };

  const handleManageOrg = (orgId: string) => {
    setActiveOrgId(orgId);
    navigate('/dashboard');
  };

  const handleReviewRequest = async (requestId: string, decision: 'approved' | 'declined') => {
    setReviewingId(requestId); setReviewError(null); setReviewSuccess(null);
    try {
      await callFunction('reviewOrgSignupRequest', {
        requestId,
        decision,
        ...(decision === 'approved' ? { color: reviewColor, joinMode: reviewJoinMode } : {}),
        ...(reviewNote.trim() ? { reviewNote: reviewNote.trim() } : {}),
      });
      setReviewSuccess(decision === 'approved'
        ? 'Approved — org created and invite email sent.'
        : 'Request declined.');
      setReviewNote('');
    } catch (err) {
      setReviewError((err as AppError).message ?? 'Review failed.');
    } finally {
      setReviewingId(null);
    }
  };

  const handleReviewOrgAdminRequest = async (requestId: string, decision: 'approved' | 'declined') => {
    setReviewingAdminReqId(requestId);
    setAdminReqError(null);
    setAdminReqSuccess(null);
    try {
      const res = await callFunction<{ success: boolean; token?: string }>('reviewOrgAdminRequest', {
        requestId,
        decision,
        ...(adminReqReviewNote.trim() ? { reviewNote: adminReqReviewNote.trim() } : {}),
      });
      if (decision === 'approved') {
        const token = (res as any)?.token as string | undefined;
        const activationUrl = token
          ? `https://aphilaty.com/admin/activate?token=${token}`
          : null;
        setAdminReqSuccess(
          activationUrl
            ? `Approved — invite email sent. Activation link (share if email doesn't arrive): ${activationUrl}`
            : 'Approved — invite email sent to requester.'
        );
      } else {
        setAdminReqSuccess('Request declined.');
      }
      setAdminReqReviewNote('');
    } catch (err) {
      setAdminReqError((err as AppError).message ?? 'Review failed.');
    } finally {
      setReviewingAdminReqId(null);
    }
  };

  const handleReviewReport = async (reportId: string, action: 'reviewed' | 'resolved' | 'dismissed') => {
    setReviewingReportId(reportId);
    setReportError(null);
    setReportSuccess(null);
    try {
      await callFunction('reviewContentReport', { reportId, action });
      setReportSuccess(
        action === 'reviewed' ? 'Report marked reviewed.'
        : action === 'resolved' ? 'Report resolved.'
        : 'Report dismissed.'
      );
      await loadContentReports(reportStatusFilter);
    } catch (err) {
      setReportError((err as AppError).message ?? 'Failed to update report.');
    } finally {
      setReviewingReportId(null);
    }
  };

  const handleIssueInvite = async (orgId: string) => {
    if (!inviteEmail.trim()) { setInviteError('Email is required.'); return; }
    setIsInviting(true); setInviteError(null); setInviteSuccess(null);
    try {
      const res = await callFunction('createOrgAdminInvite', {
        orgId, inviteeEmail: inviteEmail.trim(), role: inviteRole,
      }) as any;
      if (res?.data?.emailSent === false) {
        setInviteSuccess(`Invite created for ${inviteEmail.trim()} but the confirmation email could not be sent (SendGrid sender not verified). Share this activation link manually: https://aphilaty.com/admin/activate?token=${res.data.token}`);
      } else {
        setInviteSuccess(`Invite sent to ${inviteEmail.trim()}.`);
      }
      setInviteEmail(''); setInviteRole('admin'); setInviteOrgId(null);
    } catch (err) {
      setInviteError((err as AppError).message ?? 'Failed to send invite.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) { setCreateOrgError('Organization name is required.'); return; }
    if (!newOrgCategory) { setCreateOrgError('Category is required.'); return; }

    setIsCreatingOrg(true); setCreateOrgError(null); setCreateOrgSuccess(null);
    try {
      const result = await callFunction<{ orgId: string }>('createOrganization', {
        name: newOrgName,
        categoryId: newOrgCategory,
        color: newOrgColor,
        joinMode: newOrgJoinMode,
        deliveryMode: newOrgDeliveryMode,
      });
      setCreateOrgSuccess(`Created "${newOrgName}" (${result.orgId}). Use "Invite Admin" in the directory below to provision the admin.`);
      setNewOrgName(''); setNewOrgCategory('');
      setNewOrgJoinMode('listed'); setNewOrgColor('#4a90d9'); setNewOrgDeliveryMode('batch');
    } catch (err) {
      setCreateOrgError((err as AppError).message ?? 'Failed to create org.');
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleAssignOrgRole = async () => {
    if (!roleEmail.trim()) { setRoleError('User email is required.'); return; }
    if (!roleOrgId) { setRoleError('Select an organization.'); return; }

    setIsAssigningOrgRole(true); setRoleError(null); setRoleSuccess(null);
    try {
      const result = await callFunction<{ added: boolean }>('setOrgMemberRole', {
        orgId: roleOrgId, targetEmail: roleEmail.trim(), role: orgRole,
      });
      setRoleSuccess(result.added
        ? `Added ${roleEmail.trim()} to the org as ${orgRole}.`
        : `Updated ${roleEmail.trim()}'s role to ${orgRole}.`);
    } catch (err) {
      setRoleError(friendlyUserError(err, roleEmail.trim()));
    } finally {
      setIsAssigningOrgRole(false);
    }
  };

  const handleAssignPlatformRole = async () => {
    if (!roleEmail.trim()) { setRoleError('User email is required.'); return; }

    setIsAssigningPlatformRole(true); setRoleError(null); setRoleSuccess(null);
    try {
      await callFunction('setPlatformRole', { email: roleEmail.trim(), role: platformRole });
      setRoleSuccess(`Set ${roleEmail.trim()}'s platform role to ${platformRole}.`);
    } catch (err) {
      setRoleError(friendlyUserError(err, roleEmail.trim()));
    } finally {
      setIsAssigningPlatformRole(false);
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Platform Admin</h1>

      {/* ── Org Signup Requests ───────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">
            Org Signup Requests
            {signupRequests.length > 0 && (
              <span className="badge badge-primary" style={{ marginLeft: 8 }}>
                {signupRequests.length}
              </span>
            )}
          </span>
        </div>
        {reviewError && <div className="error-banner" role="alert" style={{ margin: '8px 16px 0' }}>{reviewError}</div>}
        {reviewSuccess && <div className="success-banner" role="status" style={{ margin: '8px 16px 0' }}>{reviewSuccess}</div>}
        {signupRequests.length === 0 ? (
          <div className="empty-state">No pending requests.</div>
        ) : (
          signupRequests.map(req => (
            <div key={req.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{req.orgName}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {req.requesterName} &lt;{req.requesterEmail}&gt; · {req.categoryId}
                  </div>
                  {req.description && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4,
                      fontStyle: 'italic' }}>
                      "{req.description}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="color" value={reviewColor}
                    onChange={e => setReviewColor(e.target.value)}
                    title="Org color" style={{ height: 30, width: 40, padding: 2, cursor: 'pointer' }} />
                  <select className="form-select" style={{ fontSize: 12, padding: '4px 8px' }}
                    value={reviewJoinMode}
                    onChange={e => setReviewJoinMode(e.target.value as 'listed' | 'open' | 'private')}>
                    <option value="listed">listed</option>
                    <option value="open">open</option>
                    <option value="private">private</option>
                  </select>
                  <button className="btn btn-primary btn-sm"
                    disabled={reviewingId === req.id}
                    onClick={() => handleReviewRequest(req.id, 'approved')}>
                    {reviewingId === req.id ? <span className="spinner" /> : 'Approve'}
                  </button>
                  <button className="btn btn-outline btn-sm"
                    style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                    disabled={reviewingId === req.id}
                    onClick={() => handleReviewRequest(req.id, 'declined')}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Org Admin Requests (AD-10 Path B) ───────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">
            Existing-Org Admin Requests
            {orgAdminRequests.length > 0 && (
              <span className="badge badge-primary" style={{ marginLeft: 8 }}>
                {orgAdminRequests.length}
              </span>
            )}
          </span>
        </div>
        {adminReqError && (
          <div className="error-banner" role="alert" style={{ margin: '8px 16px 0' }}>
            {adminReqError}
          </div>
        )}
        {adminReqSuccess && (
          <div className="success-banner" role="status" style={{ margin: '8px 16px 0' }}>
            {adminReqSuccess}
          </div>
        )}
        {orgAdminRequests.length === 0 ? (
          <div className="empty-state">No pending requests.</div>
        ) : (
          orgAdminRequests.map(req => (
            <div key={req.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{req.orgName}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {req.requesterName} &lt;{req.requesterEmail}&gt;
                    {req.domainMismatchAcknowledged && (
                      <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 10 }}>
                        domain mismatch
                      </span>
                    )}
                  </div>
                  {req.roleDescription && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4,
                      fontStyle: 'italic' }}>
                      "{req.roleDescription}"
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={reviewingAdminReqId === req.id}
                    onClick={() => handleReviewOrgAdminRequest(req.id, 'approved')}
                  >
                    {reviewingAdminReqId === req.id ? <span className="spinner" /> : 'Approve'}
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                    disabled={reviewingAdminReqId === req.id}
                    onClick={() => handleReviewOrgAdminRequest(req.id, 'declined')}
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Content Reports (A4/A5) ───────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className="card-title">
            Content Reports
            {reportStatusFilter === 'open' && contentReports.length > 0 && (
              <span className="badge badge-primary" style={{ marginLeft: 8 }}>
                {contentReports.length}
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-select" style={{ fontSize: 12, padding: '4px 8px' }}
              value={reportStatusFilter}
              onChange={e => setReportStatusFilter(e.target.value as ReportStatus)}>
              {REPORT_STATUSES.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
            <button className="btn btn-outline btn-sm" disabled={isLoadingReports}
              onClick={() => loadContentReports(reportStatusFilter)}>
              {isLoadingReports ? <span className="spinner" /> : 'Refresh'}
            </button>
          </div>
        </div>
        {reportError && <div className="error-banner" role="alert" style={{ margin: '8px 16px 0' }}>{reportError}</div>}
        {reportSuccess && <div className="success-banner" role="status" style={{ margin: '8px 16px 0' }}>{reportSuccess}</div>}
        {contentReports.length === 0 ? (
          <div className="empty-state">
            {isLoadingReports ? 'Loading…' : `No ${reportStatusFilter} reports.`}
          </div>
        ) : (
          contentReports.map(report => (
            <div key={report.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {REPORT_REASON_LABELS[report.reason] ?? report.reason}
                    <span className={`badge ${report.status === 'open' ? 'badge-warning' : 'badge-primary'}`}
                      style={{ marginLeft: 8, fontSize: 10 }}>
                      {report.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {report.contentType} · {report.orgName}
                    {report.contentTitle && <> · "{report.contentTitle}"</>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2,
                    fontFamily: 'monospace' }}>
                    {report.contentType}s/{report.contentId}
                  </div>
                  {report.details && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4,
                      fontStyle: 'italic' }}>
                      "{report.details}"
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    Reported {report.reportedAt ? new Date(report.reportedAt).toLocaleString() : '—'}
                    {report.reviewedAt && <> · Reviewed {new Date(report.reviewedAt).toLocaleString()}</>}
                  </div>
                </div>
                {(report.status === 'open' || report.status === 'reviewed') && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {report.status === 'open' && (
                      <button className="btn btn-outline btn-sm"
                        disabled={reviewingReportId === report.id}
                        onClick={() => handleReviewReport(report.id, 'reviewed')}>
                        {reviewingReportId === report.id ? <span className="spinner" /> : 'Mark Reviewed'}
                      </button>
                    )}
                    <button className="btn btn-primary btn-sm"
                      disabled={reviewingReportId === report.id}
                      onClick={() => handleReviewReport(report.id, 'resolved')}>
                      Resolve
                    </button>
                    <button className="btn btn-outline btn-sm"
                      style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                      disabled={reviewingReportId === report.id}
                      onClick={() => handleReviewReport(report.id, 'dismissed')}>
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Platform Settings ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16, padding: 18 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Platform Settings</h2>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          Controls platform-wide behaviour across all orgs.
        </p>
        {settingsError && <div className="error-banner" role="alert">{settingsError}</div>}
        {settingsSuccess && (
          <div className="success-banner" role="status" style={{ marginBottom: 10 }}>{settingsSuccess}</div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 200px' }}>
            <label className="form-label" htmlFor="auto-post-delay">Auto-post delay (seconds)</label>
            <input id="auto-post-delay" type="number" className="form-input" min={0}
              value={autoPostDelayInput}
              onChange={e => setAutoPostDelayInput(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 220px' }}>
            <label className="form-label" htmlFor="default-delivery-mode">Default delivery mode</label>
            <select id="default-delivery-mode" className="form-select"
              value={defaultDeliveryModeInput}
              onChange={e => setDefaultDeliveryModeInput(e.target.value as '' | 'immediate' | 'batch')}>
              <option value="">Per-org / org-type logic</option>
              <option value="immediate">Override → immediate (testing)</option>
              <option value="batch">Override → batch (midnight ET)</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleSaveSettings} disabled={isSavingSettings}>
            {isSavingSettings ? <span className="spinner" /> : 'Save'}
          </button>
        </div>
        <p style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-secondary)' }}>
          <strong>Auto-post delay:</strong> 0 = post as soon as AI scan clears (~5 min scheduler floor). Set to 3600 for the 1-hour review buffer at go-live.{' '}
          <strong>Delivery mode:</strong> "Per-org" uses each org's own setting (production default). "immediate" overrides all orgs — use for testing.
        </p>
      </div>

      {/* ── PII Detection Settings ────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16, padding: 18 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>PII Detection</h2>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          SSN and credit card numbers are always blocked and cannot be configured. Phone and email can be set to "warn and block" (default) or "warn and allow" (user sees a warning but can proceed).
        </p>
        {piiError && <div className="error-banner" role="alert">{piiError}</div>}
        {piiSuccess && <div className="success-banner" role="status" style={{ marginBottom: 10 }}>{piiSuccess}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Type</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Setting</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '8px 8px' }}>SSN (Social Security Number)</td>
              <td style={{ padding: '8px 8px', color: 'var(--color-text-secondary)' }}>Always blocked (locked)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '8px 8px' }}>Credit card number</td>
              <td style={{ padding: '8px 8px', color: 'var(--color-text-secondary)' }}>Always blocked (locked)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '8px 8px' }}>Phone number</td>
              <td style={{ padding: '8px 8px' }}>
                <select className="form-select" style={{ width: 'auto' }} value={piiPhone}
                  onChange={e => setPiiPhone(e.target.value as 'warn_and_block' | 'warn_and_allow')}>
                  <option value="warn_and_block">Warn and block</option>
                  <option value="warn_and_allow">Warn and allow</option>
                </select>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '8px 8px' }}>Email address</td>
              <td style={{ padding: '8px 8px' }}>
                <select className="form-select" style={{ width: 'auto' }} value={piiEmail}
                  onChange={e => setPiiEmail(e.target.value as 'warn_and_block' | 'warn_and_allow')}>
                  <option value="warn_and_block">Warn and block</option>
                  <option value="warn_and_allow">Warn and allow</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
        <button className="btn btn-primary" onClick={handleSavePii} disabled={isSavingPii}>
          {isSavingPii ? <span className="spinner" /> : 'Save PII Settings'}
        </button>
      </div>

      {/* ── Create Organization ───────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16, padding: 18 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Create Organization</h2>
        {createOrgError && <div className="error-banner" role="alert">{createOrgError}</div>}
        {createOrgSuccess && (
          <div className="success-banner" role="status" style={{ marginBottom: 10 }}>{createOrgSuccess}</div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="org-name">Name</label>
            <input id="org-name" type="text" className="form-input" value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)} placeholder="Krueger Elementary PTA" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="org-category">Category</label>
            <select id="org-category" className="form-select" value={newOrgCategory}
              onChange={e => setNewOrgCategory(e.target.value)}>
              <option value="">Select…</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="org-color">Color</label>
            <input id="org-color" type="color" className="form-input" value={newOrgColor}
              onChange={e => setNewOrgColor(e.target.value)} style={{ height: 38, padding: 4 }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="org-join-mode">Join Mode</label>
            <select id="org-join-mode" className="form-select" value={newOrgJoinMode}
              onChange={e => setNewOrgJoinMode(e.target.value as typeof newOrgJoinMode)}>
              {JOIN_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
              {newOrgJoinMode === 'open' && 'Anyone can join — no code needed.'}
              {newOrgJoinMode === 'listed' && 'Visible in directory — invite code required.'}
              {newOrgJoinMode === 'private' && 'Hidden — invite code or QR only.'}
            </p>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="org-delivery-mode">Delivery Mode</label>
            <select id="org-delivery-mode" className="form-select" value={newOrgDeliveryMode}
              onChange={e => setNewOrgDeliveryMode(e.target.value as 'immediate' | 'batch')}>
              <option value="batch">batch (midnight ET)</option>
              <option value="immediate">immediate</option>
            </select>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
              {newOrgDeliveryMode === 'batch' && 'Content held and delivered once daily at midnight.'}
              {newOrgDeliveryMode === 'immediate' && 'Content delivered as soon as it clears review.'}
            </p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleCreateOrg} disabled={isCreatingOrg}>
          {isCreatingOrg ? <span className="spinner" /> : 'Create Organization'}
        </button>
      </div>

      {/* ── Organization Directory ────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">All Organizations ({orgs.length})</span>
          <input type="text" className="form-input" placeholder="Filter by name or ID…"
            value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            style={{ maxWidth: 240 }} />
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>ID</th>
              <th>Join Mode</th>
              <th>Delivery</th>
              <th>Admins</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map(org => (
              <>
                <tr key={org.id}>
                  <td>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                      background: org.color ?? '#999', marginRight: 8, verticalAlign: 'middle' }} />
                    {org.name}
                    {org.isSystem && <span className="badge badge-gray" style={{ marginLeft: 6 }}>system</span>}
                    {(org.adminUids?.length ?? 0) === 0 && (
                      <span className="badge badge-warning" style={{ marginLeft: 6 }}>no admin</span>
                    )}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                    {org.id}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{org.joinMode}</td>
                  <td>
                    <select
                      className="form-select"
                      style={{ fontSize: 12, padding: '2px 6px', minWidth: 100 }}
                      value={org.deliveryMode ?? 'batch'}
                      onChange={async e => {
                        try {
                          await updateDoc(doc(db, `organizations/${org.id}`), { deliveryMode: e.target.value });
                        } catch (err) {
                          console.error('[deliveryMode update]', err);
                        }
                      }}
                    >
                      <option value="batch">batch</option>
                      <option value="immediate">immediate</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ fontSize: 11, padding: '2px 6px' }}
                      onClick={() =>
                        setExpandedAdminOrgId(expandedAdminOrgId === org.id ? null : org.id)
                      }
                    >
                      {org.adminUids?.length ?? 0}{' '}
                      {expandedAdminOrgId === org.id ? '▲' : '▼'}
                    </button>
                  </td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleManageOrg(org.id)}>
                      Manage
                    </button>
                    <button className="btn btn-outline btn-sm"
                      onClick={() => {
                        setInviteOrgId(inviteOrgId === org.id ? null : org.id);
                        setInviteEmail(''); setInviteRole('admin');
                        setInviteError(null); setInviteSuccess(null);
                      }}>
                      Invite Admin
                    </button>
                  </td>
                </tr>
                {expandedAdminOrgId === org.id && (
                  <tr key={`${org.id}-admins`}>
                    <td colSpan={6} style={{ background: 'var(--color-bg-secondary)', padding: '10px 16px' }}>
                      <AdminListSection
                        orgId={org.id}
                        orgName={org.name}
                        viewerIsPlatformAdmin={isPlatformAdmin}
                        viewerUid={user?.uid ?? ''}
                        showInvite={false}
                      />
                    </td>
                  </tr>
                )}
                {inviteOrgId === org.id && (
                  <tr key={`${org.id}-invite`}>
                    <td colSpan={6} style={{ background: 'var(--color-bg-secondary)', padding: '10px 16px' }}>
                      {inviteError && (
                        <div className="error-banner" role="alert" style={{ marginBottom: 8 }}>{inviteError}</div>
                      )}
                      {inviteSuccess && (
                        <div className="success-banner" role="status" style={{ marginBottom: 8 }}>{inviteSuccess}</div>
                      )}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ marginBottom: 0, flex: '2 1 200px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>
                            Invite admin to <strong>{org.name}</strong>
                          </label>
                          <input type="email" className="form-input"
                            value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                            placeholder="admin@example.com" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0, flex: '1 1 140px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Role</label>
                          <select className="form-select" value={inviteRole}
                            onChange={e => setInviteRole(e.target.value as InviteRole)}>
                            {INVITE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <button className="btn btn-primary btn-sm"
                          disabled={isInviting} onClick={() => handleIssueInvite(org.id)}>
                          {isInviting ? <span className="spinner" /> : 'Send Invite'}
                        </button>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => setInviteOrgId(null)}>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {filteredOrgs.length === 0 && (
              <tr><td colSpan={6}><div className="empty-state">No organizations match.</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Flagged Private Groups ───────────────────────────────────── */}
      {flaggedPrivateOrgs.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--color-warning, #f59e0b)' }}>
          <div className="card-header">
            <span className="card-title">⚠️ Flagged for Review ({flaggedPrivateOrgs.length})</span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '0 16px 8px' }}>
            Private user groups flagged via content moderation or legal hold. Not a routine browse list —
            only orgs with an active <code>monitoringFlag</code> appear here.
          </p>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>ID</th><th>Type</th><th>Flag</th><th></th></tr>
            </thead>
            <tbody>
              {flaggedPrivateOrgs.map(org => (
                <tr key={org.id}>
                  <td>{org.name}</td>
                  <td style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>{org.id}</td>
                  <td style={{ fontSize: 12 }}>{org.isStarterOrg ? 'My Family' : 'User group'}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-warning, #b45309)' }}>{org.monitoringFlag}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => handleManageOrg(org.id)}>Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── User & Role Management ────────────────────────────────────── */}
      <div className="card" style={{ padding: 18 }}>
        <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Assign Roles</h2>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          Looks up an existing user by email — adds them to an org (or changes their role
          there), or grants/revokes platform-level access.
        </p>
        {roleError && <div className="error-banner" role="alert">{roleError}</div>}
        {roleSuccess && (
          <div className="success-banner" role="status" style={{ marginBottom: 10 }}>{roleSuccess}</div>
        )}
        <div className="form-group">
          <label className="form-label" htmlFor="role-email">User Email</label>
          <input id="role-email" type="email" className="form-input" value={roleEmail}
            onChange={e => setRoleEmail(e.target.value)} placeholder="user@example.com" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 14, alignItems: 'end',
          marginBottom: 14 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="role-org">Organization</label>
            <select id="role-org" className="form-select" value={roleOrgId}
              onChange={e => setRoleOrgId(e.target.value)}>
              <option value="">Select…</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="role-org-role">Org Role</label>
            <select id="role-org-role" className="form-select" value={orgRole}
              onChange={e => setOrgRole(e.target.value as typeof orgRole)}>
              {ORG_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button className="btn btn-outline" onClick={handleAssignOrgRole} disabled={isAssigningOrgRole}>
            {isAssigningOrgRole ? <span className="spinner" /> : 'Assign Org Role'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 14, alignItems: 'end' }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Platform role — grants access across <em>every</em> org (or revokes it back to "member").
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="role-platform-role">Platform Role</label>
            <select id="role-platform-role" className="form-select" value={platformRole}
              onChange={e => setPlatformRole(e.target.value as typeof platformRole)}>
              {PLATFORM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button className="btn btn-outline" onClick={handleAssignPlatformRole} disabled={isAssigningPlatformRole}
            style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
            {isAssigningPlatformRole ? <span className="spinner" /> : 'Assign Platform Role'}
          </button>
        </div>
      </div>
    </div>
  );
}
